import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import { ThemeProvider } from './context/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';

import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ArticlePage from './pages/ArticlePage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import BookmarksPage from './pages/BookmarksPage';

import ReaderDashboard from './pages/reader/ReaderDashboard';
import PublishArticle from './pages/reader/PublishArticle';
import MyArticles from './pages/reader/MyArticles';
import ArticleReview from './pages/reader/ArticleReview';
import SubmitAd from './pages/reader/SubmitAd';
import MyAds from './pages/reader/MyAds';

import EditorDashboard from './pages/editor/EditorDashboard';
import EditorArticle from './pages/editor/EditorArticle';

import ProducerDashboard from './pages/producer/ProducerDashboard';
import ProducerArticleReview from './pages/producer/ProducerArticleReview';

import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminArticles from './pages/admin/AdminArticles';
import AdminActivity from './pages/admin/AdminActivity';
import AdminLayoutManager from './pages/admin/AdminLayoutManager';
import AdminPayments from './pages/admin/AdminPayments';
import AdminCategories from './pages/admin/AdminCategories';
import AdminBanner from './pages/admin/AdminBanner';
import AdminSettings from './pages/admin/AdminSettings';
import AdminStorage from './pages/admin/AdminStorage';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loading">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    const dest = { reader: '/dashboard', editor: '/editor', producer: '/producer', admin: '/admin' };
    return <Navigate to={dest[user.role] || '/'} replace />;
  }
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <SiteSettingsProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '14px',
                  borderRadius: '8px',
                  background: '#FFFFFF',
                  color: '#0D1B2A',
                  border: '1px solid rgba(0,74,159,0.15)',
                  boxShadow: '0 4px 16px rgba(0,74,159,0.1)',
                },
                success: { iconTheme: { primary: '#059669', secondary: '#fff' } },
                error:   { iconTheme: { primary: '#DC2626', secondary: '#fff' } },
              }}
            />
            <Routes>
              {/* Public */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/berita/:slug" element={<ArticlePage />} />
              <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
              <Route path="/lupa-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/profil" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/bookmark" element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>} />

              {/* Reader */}
              <Route path="/dashboard" element={<ProtectedRoute roles={['reader','admin']}><ReaderDashboard /></ProtectedRoute>} />
              <Route path="/publikasi" element={<ProtectedRoute roles={['reader','admin']}><PublishArticle /></ProtectedRoute>} />
              <Route path="/artikel-saya" element={<ProtectedRoute roles={['reader','admin']}><MyArticles /></ProtectedRoute>} />
              <Route path="/artikel-saya/:id/review" element={<ProtectedRoute roles={['reader','admin']}><ArticleReview /></ProtectedRoute>} />
              <Route path="/pasang-iklan" element={<ProtectedRoute roles={['reader','admin']}><SubmitAd /></ProtectedRoute>} />
              <Route path="/iklan-saya" element={<ProtectedRoute roles={['reader','admin']}><MyAds /></ProtectedRoute>} />

              {/* Editor */}
              <Route path="/editor" element={<ProtectedRoute roles={['editor','admin']}><EditorDashboard /></ProtectedRoute>} />
              <Route path="/editor/artikel/:id" element={<ProtectedRoute roles={['editor','admin']}><EditorArticle /></ProtectedRoute>} />

              {/* Producer */}
              <Route path="/producer" element={<ProtectedRoute roles={['producer','admin']}><ProducerDashboard /></ProtectedRoute>} />
              <Route path="/producer/artikel/:id" element={<ProtectedRoute roles={['producer','admin']}><ProducerArticleReview /></ProtectedRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute roles={['admin']}><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/articles" element={<ProtectedRoute roles={['admin']}><AdminArticles /></ProtectedRoute>} />
              <Route path="/admin/aktivitas" element={<ProtectedRoute roles={['admin']}><AdminActivity /></ProtectedRoute>} />
              <Route path="/admin/pembayaran" element={<ProtectedRoute roles={['admin']}><AdminPayments /></ProtectedRoute>} />
              <Route path="/admin/tata-letak" element={<ProtectedRoute roles={['admin']}><AdminLayoutManager /></ProtectedRoute>} />
              <Route path="/admin/categories" element={<ProtectedRoute roles={['admin']}><AdminCategories /></ProtectedRoute>} />
              <Route path="/admin/banner" element={<ProtectedRoute roles={['admin']}><AdminBanner /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute roles={['admin']}><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/storage" element={<ProtectedRoute roles={['admin']}><AdminStorage /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </SiteSettingsProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
